import client from './client';

// Types mirroring models/analytics_recipient.go and models/report_schedule.go.

export type RiskTier = 'repeat_offender' | 'high' | 'medium' | 'low';

export interface RecipientRiskSummary {
  email: string;
  first_name: string;
  last_name: string;
  position: string;
  total_sent: number;
  total_opened: number;
  total_clicked: number;
  total_submitted: number;
  total_reported: number;
  click_rate: number;
  submit_rate: number;
  total_campaigns: number;
  campaigns_clicked: number;
  last_event_at: string;
  risk_tier: RiskTier;
}

export interface RecipientHistoryEntry {
  campaign_id: number;
  campaign_name: string;
  email: string;
  message: string;
  time: string;
  details: string;
}

export type ReportFrequency = 'weekly' | 'monthly';
export type ReportFormat = 'pdf' | 'csv';
export type ReportScope = 'all' | 'campaign';

export interface ReportSchedule {
  id: number;
  name: string;
  frequency: ReportFrequency;
  day_of_week: number;
  day_of_month: number;
  recipients: string;
  format: ReportFormat;
  scope: ReportScope;
  campaign_id: number;
  last_run_at: string | null;
  next_run_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReportScheduleInput {
  name: string;
  frequency: ReportFrequency;
  day_of_week?: number;
  day_of_month?: number;
  recipients?: string;
  format: ReportFormat;
  scope: ReportScope;
  campaign_id?: number;
}

export interface GeneratedReport {
  id: number;
  schedule_id: number;
  name: string;
  filename: string;
  format: ReportFormat;
  created_at: string;
}

export const recipientRiskApi = {
  getSummaries: (tier?: RiskTier) =>
    client.get<RecipientRiskSummary[]>('/analytics/recipients/risk', {
      params: { tier: tier || undefined },
    }),

  getAtRisk: () =>
    client.get<RecipientRiskSummary[]>('/analytics/recipients/at-risk'),

  getHistory: (email: string) =>
    client.get<RecipientHistoryEntry[]>(
      `/analytics/recipients/${encodeURIComponent(email)}/history`
    ),
};

export const reportsApi = {
  listSchedules: () => client.get<ReportSchedule[]>('/reports/schedules'),

  createSchedule: (input: ReportScheduleInput) =>
    client.post<ReportSchedule>('/reports/schedules', input),

  getSchedule: (id: number) => client.get<ReportSchedule>(`/reports/schedules/${id}`),

  deleteSchedule: (id: number) => client.delete(`/reports/schedules/${id}`),

  runSchedule: (id: number) =>
    client.post<GeneratedReport>(`/reports/schedules/${id}/run`),

  listGenerated: () => client.get<GeneratedReport[]>('/reports/'),

  // Download via the axios client so the Authorization header is attached,
  // then hand the blob to the browser as a file.
  download: async (report: GeneratedReport) => {
    const res = await client.get<Blob>(`/reports/${report.id}/download`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = report.filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};
